import { FC, useMemo, type JSX } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Select } from '../components/Select';
import { ImageType, OfferedProductType, OptionType, WantedProductType } from '../types/SellTradeForm.types';
import { onlyNumbers, preventMinuses } from '../utils/inputUtils';
import { CloseIcon } from '../../../icons';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { customStyles } from './OffererForm';
import { Box, Typography } from '@mui/material';
import { UploadFile } from '../../ServicePage/ContactService/UploadFile';
import { Files, FileWrapper } from '../../ServicePage/ContactService/styles';
import addAdditionalWatchIcon from '../../../assets/images/svg/addAdditionalWatchIcon.svg';
import Image from 'next/image';

export type Props = {
  brandsWanted: OptionType[];
  brandsOffered: OptionType[];
  modelsWanted: OptionType[][];
  modelsOffered: OptionType[][];
  currencyOptions: OptionType[];
  offeredProducts: OfferedProductType[];
  wantedProducts: WantedProductType[];
  manageOfferedProducts: (
    propName: keyof OfferedProductType,
    payload: string | number | boolean | JSX.Element | ImageType[],
    index: number
  ) => void;
  manageWantedProducts: (
    propName: keyof WantedProductType,
    payload: string | number | boolean | JSX.Element | ImageType[],
    index: number
  ) => void;
  addProduct: (productType: string) => void;
  forModal?: boolean;
  handleBlur: (key: string, value: string, which: string, index: number, request?: boolean) => void;
  removeImage: (image: ImageType, index: number) => void;
  step?: number;
  setStep?: (option: number) => void;
};

export const WatchesTradeForm: FC<Props> = ({
  brandsWanted,
  brandsOffered,
  modelsWanted,
  modelsOffered,
  currencyOptions,
  offeredProducts,
  wantedProducts,
  manageOfferedProducts,
  manageWantedProducts,
  addProduct,
  forModal,
  handleBlur,
  removeImage,
  step,
  setStep,
}) => {
  const locale = useAppSelector((st) => st.locale);

  const messages = useMemo(() => {
    return {
      trade_form_step_2: locale['trade_form_step_2'],
      trade_form_step_number: locale['trade_form_step_number'],
      trade_form_brand_title: locale['trade_form_brand_title'],
      trade_form_brand_placeholder: locale['trade_form_brand_placeholder'],
      trade_form_model_title: locale['trade_form_model_title'],
      trade_form_model_placeholder: locale['trade_form_model_placeholder'],
      trade_form_reference_title: locale['trade_form_reference_title'],
      trade_form_reference_placeholder: locale['trade_form_reference_placeholder'],
      trade_form_trade_more: locale['trade_form_trade_more'],
      trade_form_add_more_watch_title: locale['trade_form_add_more_watch_title'],
      trade_form_step_3: locale['trade_form_step_3'],
      trade_in_form_condition_title: locale['trade_in_form_condition_title'],
      trade_in_form_condition_placeholder: locale['trade_in_form_condition_placeholder'],
      trade_form_box_papers_title: locale['trade_form_box_papers_title'],
      trade_form_box_checkbox: locale['trade_form_box_checkbox'],
      trade_form_papers_checkbox: locale['trade_form_papers_checkbox'],
      trade_form_add_photos_title: locale['trade_form_add_photos_title'],
      trade_form_add_photos_info: locale['trade_form_add_photos_info'],
      trade_form_price_title: locale['trade_form_price_title'],
      trade_form_price_placeholder: locale['trade_form_price_placeholder'],
      trade_form_currency_title: locale['trade_form_currency_title'],
      trade_form_currency_placeholder: locale['trade_form_currency_placeholder'],
      trade_form_comments_title: locale['trade_form_comments_title'],
      trade_form_comments_placeholder: locale['trade_form_comments_placeholder'],
      conditions: [
        { value: 0, label: locale['sell_trade_types_condition_new'] },
        { value: 1, label: locale['sell_trade_types_condition_mint'] },
        { value: 2, label: locale['sell_trade_types_condition_old'] },
      ],
      attachment: locale['general_attachment_star'],
    };
  }, [locale]);
  return (
    <div className='form-item-container'>
      {!forModal && step === 2 ? (
        <>
          {wantedProducts?.map((wanted, index) => (
            <div className='form-item-container' key={index}>
              {wantedProducts?.length > 1 ? (
                <>
                  <span>{`${messages.trade_form_step_number} ${index + 1}.`}</span>
                </>
              ) : null}
              <div className='form-row form-flex-row'>
                <div className='input-wrapper'>
                  <Select
                    options={brandsWanted}
                    value={brandsWanted?.find((item) => item.label === wanted?.brandName)}
                    onChange={(option) => {
                      manageWantedProducts('brandName', option?.label, index);
                      handleBlur('brandName', option?.label, 'wanted', index, true);
                    }}
                    onBlur={(e) => {
                      e.target.value &&
                        !brandsWanted?.filter(
                          (item) =>
                            typeof item?.label === 'string' &&
                            item?.label?.toLowerCase()?.includes(e.target.value?.toLowerCase())
                        )?.length &&
                        manageWantedProducts('brandName', e.target.value, index);
                      handleBlur('brandName', e.target.value, 'wanted', index);
                    }}
                    className='custom-select'
                    placeholder={`${messages.trade_form_brand_placeholder}`}
                    dropdownStyles={customStyles}
                    isClearable
                    loadingText='Brands are loading...'
                  />
                </div>
                <div className='input-wrapper  input-wrapper-right'>
                  <Select
                    options={modelsWanted[index]}
                    value={modelsWanted?.[index]?.find((item) => item.label === wanted?.productName) || null}
                    onChange={(option) => {
                      manageWantedProducts('productName', option?.label, index);
                      manageWantedProducts('productId', option.id, index);
                    }}
                    className='custom-select'
                    placeholder={`${messages.trade_form_model_placeholder}`}
                    dropdownStyles={customStyles}
                    isClearable
                    loadingText='Models are loading...'
                  />
                </div>
              </div>
              <div className='form-row '>
                <div className='input-wrapper'>
                  <input
                    type='text'
                    value={wanted?.refNumber}
                    onChange={(e) => manageWantedProducts('refNumber', e.target.value, index)}
                    className='custom-input'
                    placeholder={`${messages.trade_form_reference_placeholder}`}
                  />
                </div>
              </div>
            </div>
          ))}
          <div className='input-label clickable-label clickable-label-step-2' onClick={() => addProduct('wanted')}>
            <Image src={addAdditionalWatchIcon.src} alt='additional watch' width={25} height={25} />
            <span className='add-watch-span'> {`${messages.trade_form_add_more_watch_title}`}</span>
          </div>
        </>
      ) : null}
      {forModal || step === 3
        ? offeredProducts?.map((offered, index) => (
            <div className='form-item-container' key={index}>
              {offeredProducts?.length > 1 ? (
                <>
                  <span>{`${messages.trade_form_step_number} ${index + 1}`}</span>
                  <hr className='custom-hr gray-hr' />
                </>
              ) : null}
              <div className={`${forModal ? 'form-row form-row-for-modal' : 'form-row form-flex-row'}`}>
                <div className='input-wrapper'>
                  <Select
                    options={brandsOffered}
                    value={brandsOffered?.find((item) => item?.label === offered?.brandName)}
                    onChange={(option) => {
                      manageOfferedProducts('brandName', option?.label, index);
                      handleBlur('brandName', option?.label, 'offered', index, true);
                    }}
                    onBlur={(e) => {
                      e.target.value &&
                        !brandsOffered?.filter(
                          (item) =>
                            typeof item?.label === 'string' &&
                            item?.label?.toLowerCase()?.includes(e.target.value?.toLowerCase())
                        )?.length &&
                        manageOfferedProducts('brandName', e.target.value, index);
                      handleBlur('brandName', e.target.value || offered.brandName, 'offered', index);
                    }}
                    className='custom-select'
                    placeholder={`${messages.trade_form_brand_placeholder}`}
                    dropdownStyles={customStyles}
                    isClearable
                    loadingText='Brands are loading...'
                  />
                </div>
                <div className='input-wrapper  input-wrapper-right'>
                  <Select
                    options={modelsOffered[index]}
                    value={modelsOffered?.[index]?.find((item) => item.label === offered?.productName) || null}
                    onChange={(option) => manageOfferedProducts('productName', option?.label, index)}
                    onBlur={(e) => {
                      e.target.value &&
                        !modelsOffered?.[index]?.filter(
                          (item) =>
                            typeof item?.label === 'string' &&
                            item?.label?.toLowerCase()?.includes(e.target.value?.toLowerCase())
                        )?.length &&
                        manageOfferedProducts('productName', e.target.value, index);
                      handleBlur('productName', e.target.value, 'offered', index);
                    }}
                    className='custom-select'
                    placeholder={`${messages.trade_form_model_placeholder}`}
                    dropdownStyles={customStyles}
                    isClearable
                    loadingText='Models are loading...'
                  />
                </div>
              </div>
              <div className={`${forModal ? 'form-row' : 'form-row form-flex-row'}`}>
                <div className='input-wrapper'>
                  <input
                    type='text'
                    value={offered?.refNumber}
                    onChange={(e) => manageOfferedProducts('refNumber', e.target.value, index)}
                    className='custom-input'
                    placeholder={`${messages.trade_form_reference_placeholder}`}
                  />
                </div>
                <div className='input-wrapper input-wrapper-right'>
                  <Select
                    options={messages.conditions}
                    value={messages.conditions?.find((item) => item.value === offered?.condition)}
                    onChange={(option) => {
                      manageOfferedProducts('condition', option.value, index);
                    }}
                    className='custom-select'
                    placeholder={`${messages.trade_in_form_condition_placeholder}`}
                    dropdownStyles={customStyles}
                    isClearable
                  />
                </div>
              </div>
              <hr className='custom-hr gray-hr' />
              <div className='checkbox-container'>
                <Typography
                  variant='h4'
                  style={{
                    fontSize: '24px',
                    lineHeight: '29px',
                    letterSpacing: '1px',
                    color: '#535353',
                    marginBottom: '8px',
                  }}
                >
                  {messages.trade_form_box_papers_title}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      className='checkbox'
                      checked={offered?.hasBox}
                      onChange={(e) => manageOfferedProducts('hasBox', e.target.checked, index)}
                    />
                  }
                  label={<label className='input-label big-label'>{`${messages.trade_form_box_checkbox}`}</label>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      className='checkbox'
                      checked={offered?.hasPaper}
                      onChange={(e) => manageOfferedProducts('hasPaper', e.target.checked, index)}
                    />
                  }
                  label={<label className='input-label big-label'>{`${messages.trade_form_papers_checkbox}`}</label>}
                />
              </div>
              <hr className='custom-hr gray-hr-bottom' />
              <Box mb={1} className='upload-files'>
                <Typography
                  variant='body1'
                  fontSize='16px'
                  letterSpacing={2}
                  marginBottom='12px'
                  lineHeight='20px !important'
                  fontWeight='400 !important'
                >
                  {messages.attachment}
                </Typography>
                <UploadFile
                  minHeight='88px'
                  getFiles={(files: ImageType[]) => {
                    manageOfferedProducts('images', files || [], index);
                  }}
                  title={locale.general_upload_file}
                />
                <div style={{ position: 'relative' }}>
                  <Files>
                    {offered?.images?.map((image) => (
                      <FileWrapper style={{ position: 'relative' }}>
                        <span style={{}}>{`${image?.name.slice(0, 20)}${image?.name?.length > 20 ? '...' : ''}`}</span>
                        <div onClick={() => removeImage(image, index)}>
                          <CloseIcon size={12} />
                        </div>
                      </FileWrapper>
                    ))}
                  </Files>
                </div>
              </Box>
              <div className='group-container price-container'>
                <input
                  type='number'
                  value={offered?.price}
                  onChange={(e) => manageOfferedProducts('price', e.target.value, index)}
                  onKeyDown={(e) => {
                    onlyNumbers(e);
                    preventMinuses(e);
                  }}
                  className={`${forModal ? 'custom-input currency-input-for-modal' : 'custom-input currency-input'}`}
                  placeholder={`${messages.trade_form_price_placeholder}`}
                  min={0}
                />
                <Select
                  options={currencyOptions}
                  value={
                    currencyOptions.find((item) => item.id === offered?.currency) ||
                    currencyOptions.find((item) => item.id === 'EUR')
                  }
                  onChange={(option) => manageOfferedProducts('currency', option?.label, index)}
                  className='custom-select currency-select'
                  placeholder={`${messages.trade_form_currency_placeholder}`}
                  dropdownStyles={customStyles}
                  isClearable={false}
                />
              </div>
              <textarea
                className='custom-input text-area'
                value={offered?.comment}
                onChange={(e) => manageOfferedProducts('comment', e.target.value, index)}
                placeholder={`${messages.trade_form_comments_placeholder}`}
              />
            </div>
          ))
        : null}

      {forModal ? (
        <div className='input-label clickable-label step-3-for-modal' onClick={() => addProduct('offered')}>
          <Image src={addAdditionalWatchIcon.src} alt='Add' width={25} height={25} />
          <span className='add-watch-span-for-modal'> {`${messages.trade_form_add_more_watch_title}`}</span>
        </div>
      ) : step === 3 ? (
        <div className='input-label clickable-label clickable-label-step-3' onClick={() => addProduct('offered')}>
          <Image src={addAdditionalWatchIcon.src} alt='Add' width={25} height={25} />
          <span className='add-watch-span'> {`${messages.trade_form_add_more_watch_title}`}</span>
        </div>
      ) : null}
    </div>
  );
};
